import client from "@/app/api";
import { useEffect, useState, useRef } from "react";
import { UUID } from "crypto";
import { useGlobalState } from "@/lib/states";
import { useWebSocket } from "@/hooks/useWebSocket";

export type WsNotification =
  | { type: "message"; message: { id: UUID; from_id: UUID; to_user_id: UUID } }
  | { type: "order"; order: { id: UUID }; status: number }
  | {
      type: "contact";
      status: "requested" | "pending" | "accepted" | "deleted";
      user: { id: UUID };
    };

type NotificationTypeMap = {
  [P in WsNotification as P["type"]]: P;
};

export type WsInMessage =
  | { type: "heartbeat" }
  | { type: "ping" }
  | WsNotification;

export type WsOutMessage =
  | { type: "pong" }
  | { type: "read_chat"; chat_id: UUID }
  | { type: "read_order"; order_id: UUID }
  | { type: "read_orders" }
  | { type: "read_contacts" };

function isNotification(msg: WsInMessage): msg is WsNotification {
  return (
    msg.type === "message" || msg.type === "order" || msg.type === "contact"
  );
}

export function useNotifications<T extends keyof NotificationTypeMap>(type: T) {
  return useGlobalState(
    "notifications_" + type,
    [] as NotificationTypeMap[T][],
  );
}

export function useUpdates<T>(state: T[], callback: (updates: T[]) => void) {
  let [prev, setPrev] = useState<Set<T>>(() => new Set());
  useEffect(() => {
    let newElements = [];
    for (let e of state) {
      if (!prev.has(e)) {
        newElements.push(e);
      }
    }
    if (newElements.length > 0) callback(newElements);
    setPrev(new Set(state));
  }, [state]);
}

export const useWs = () => {
  let [ws, _] = useGlobalState<WebSocket | null>("web_socket", null);
  return ws ? (msg: WsOutMessage) => ws!.send(JSON.stringify(msg)) : null;
};

export function useStartWs() {
  const [selectedChat] = useGlobalState<{
    type: "internal" | "whatsapp";
    id: string;
  } | null>("selected_chat", null);

  // Use a ref to access the latest selectedChat value without causing reconnects
  const selectedChatRef = useRef(selectedChat);
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const states: {
    [P in WsNotification["type"]]: ReturnType<typeof useNotifications<P>>;
  } = {
    message: useNotifications("message"),
    order: useNotifications("order"),
    contact: useNotifications("contact"),
  };

  // TODO: Temporarily disabled WebSocket connection
  // const [connectionStatus, setConnectionStatus] = useGlobalState<
  //   "connecting" | "connected" | "disconnected" | "error"
  // >("ws_connection_status", "disconnected");
  //
  // const { ws, connectionState, reconnect } = useWebSocket({
  //   url: `${client.API_URL.replace("http", "ws")}/trigger/notifier/ws`,
  //   reconnectInterval: 3000,
  //   maxReconnectAttempts: Infinity, // Keep trying forever
  //   enableLogging: false, // Set to true for debugging
  //   onConnectionChange: setConnectionStatus,
  // });
  // TODO: Temporarily disabled WebSocket - ws is set to null
  // const ws = null;
  const connectionState = "disconnected" as const;
  const reconnect = () => {};

  // useEffect(() => {
  //   if (!ws) return;
  //
  //   const handleMessage = (msg: MessageEvent) => {
  //     let data: any;
  //     try {
  //       data = JSON.parse(msg.data);
  //     } catch (e) {
  //       console.error("Failed to parse WebSocket message:", e);
  //       return;
  //     }
  //
  //     if (isNotification(data)) {
  //       // Only add notification if it's not for the current chat
  //       const currentSelectedChat = selectedChatRef.current;
  //
  //       if (
  //         data.type === "message" &&
  //         currentSelectedChat?.type === "internal" &&
  //         (data.message.from_id === currentSelectedChat.id ||
  //           data.message.to_user_id === currentSelectedChat.id)
  //       ) {
  //         return; // Skip adding notification for current chat
  //       }
  //
  //       states[data.type][1]((prev: any) => [...prev, data]);
  //     }
  //   };
  //
  //   ws.addEventListener("message", handleMessage);
  //
  //   return () => {
  //     ws.removeEventListener("message", handleMessage);
  //   };
  // }, [ws, states]);

  return { connectionState, reconnect };
}

// Hook to access connection status from anywhere in the app
export function useConnectionStatus() {
  const [connectionStatus] = useGlobalState<
    "connecting" | "connected" | "disconnected" | "error"
  >("ws_connection_status", "disconnected");
  return connectionStatus;
}

export function NotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { connectionState, reconnect } = useStartWs();
  return <>{children}</>;
}
